import { supabase } from '../lib/supabase';
import { PropertyDetails, EstimationResult } from '../types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface EstimationRequestParams {
  mode: 'internal' | 'public';
  propertyDetails: PropertyDetails;
  recaptchaToken?: string;
  providerId?: string;
  email?: string;
  phone?: string;
  source?: string;
  campaign?: string;
}

/**
 * Service pour appeler l'estimation via Edge Function Supabase
 */
export const estimationService = {
  /**
   * Estime les revenus locatifs pour un bien
   */
  estimate: async (params: EstimationRequestParams): Promise<EstimationResult> => {
    try {
      // Get client IP for rate limiting (public mode)
      let clientIp: string | undefined;
      if (params.mode === 'public') {
        // Try to get IP from a service (fallback to empty if not available)
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          clientIp = ipData.ip;
        } catch (e) {
          console.warn('Could not fetch client IP:', e);
          // Continue without IP - Edge Function will extract it from headers
        }
      }

      const requestBody: any = {
        mode: params.mode,
        propertyDetails: {
          address: params.propertyDetails.address, // Adresse complète (label) pour l'estimation
          street: params.propertyDetails.street, // Rue uniquement (name) pour créer la property
          city: params.propertyDetails.city,
          zipCode: params.propertyDetails.zipCode,
          propertyType: params.propertyDetails.propertyType,
          bedrooms: params.propertyDetails.bedrooms,
          bathrooms: params.propertyDetails.bathrooms,
          area: params.propertyDetails.area,
          maxGuests: params.propertyDetails.maxGuests,
        },
      };

      // Add public mode specific fields
      if (params.mode === 'public') {
        if (params.recaptchaToken) {
          requestBody.recaptchaToken = params.recaptchaToken;
        }
        if (params.providerId) {
          requestBody.providerId = params.providerId;
        }
        if (clientIp) {
          requestBody.clientIp = clientIp;
        }
        if (params.email) {
          requestBody.email = params.email;
        }
        if (params.phone) {
          requestBody.phone = params.phone;
        }
        if (params.source) {
          requestBody.source = params.source;
        }
        if (params.campaign) {
          requestBody.campaign = params.campaign;
        }
      }

      let data: any;
      let error: any;

      if (params.mode === 'public') {
        // For public mode, call directly with anon key (no user auth required)
        const response = await fetch(`${SUPABASE_URL}/functions/v1/estimation-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { message: errorText || `HTTP ${response.status}` };
          }
          error = errorData;
        } else {
          data = await response.json();
        }
      } else {
        // For internal mode, use supabase.functions.invoke with authentication
        const result = await supabase.functions.invoke('estimation-service', {
          body: requestBody,
        });
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('Erreur lors de l\'appel estimation:', error);
        
        // Handle specific error cases
        if (error.message?.includes('Rate limit')) {
          throw new Error('Trop de requêtes. Veuillez réessayer plus tard.');
        }
        if (error.message?.includes('reCAPTCHA')) {
          throw new Error('Validation reCAPTCHA échouée. Veuillez réessayer.');
        }
        if (error.message?.includes('providerId')) {
          throw new Error('Identifiant prestataire invalide.');
        }
        
        throw new Error(error.message || 'Erreur lors de l\'estimation');
      }

      if (!data) {
        throw new Error('Réponse invalide de l\'API d\'estimation');
      }

      // Validate response structure
      if (typeof data.isEligible === 'undefined' || 
          typeof data.lowEstimate === 'undefined' || 
          typeof data.highEstimate === 'undefined') {
        throw new Error('Format de réponse invalide');
      }

      return data as EstimationResult;
    } catch (e: any) {
      console.error('Erreur critique lors de l\'estimation:', e);
      throw new Error(e.message || 'Erreur inconnue lors de l\'estimation');
    }
  },
};
